import {
    CapsuleGeometry,
    Euler,
    IcosahedronGeometry,
    Matrix3,
    Matrix4,
    PerspectiveCamera,
    TorusGeometry,
    Vector3,
} from "three";
import { hashPlacementToUnit } from "./pathPopulatePlacement";
import { startFrameBudget, yieldIfFrameExhausted } from "./cooperativeScheduler";

const DEG_DECIMALS = 2;

/** Rounds degree values for UI and SVG (avoids long float strings from drag / atan2). */
export function roundDeg(deg: number, decimals = DEG_DECIMALS): number {
    const factor = 10 ** decimals;
    return Math.round(deg * factor) / factor;
}

export type PrimitiveType =
    | "cube"
    | "geodesic-sphere"
    | "icosahedron"
    | "torus"
    | "capsule"
    | "pyramid";
export type PrimitivePlacementMode = "alternating" | "random";
export type PrimitiveObjectRotationMode =
    | "fixed"
    | "path"
    | "radial"
    | "random"
    | "polar-around-point"
    | "polar-around-point-inverted";
export type PrimitivePointsProgressPhase =
    | "generate"
    | "rotate-project"
    | "assign";

export interface PrimitivePointsProgress {
    phase: PrimitivePointsProgressPhase;
    processed: number;
    total: number;
}

export interface PrimitiveProjectedPoint {
    x: number;
    y: number;
    depth: number;
    objectIndex: number;
    rotationDeg: number;
    faceId: string;
    flipX: boolean;
    flipY: boolean;
    isBackface: boolean;
}

export interface PrimitivePointsComputeInput {
    primitiveType: PrimitiveType;
    subdivisions: number;
    rotationXDeg: number;
    rotationYDeg: number;
    rotationZDeg: number;
    cameraFovDeg: number;
    cameraDistance: number;
    backfaceCulling: boolean;
    objectCount: number;
    objectPlacementMode: PrimitivePlacementMode;
    objectRotationMode: PrimitiveObjectRotationMode;
    polarRotationCenterXPct: number;
    polarRotationCenterYPct: number;
    positionOffsetRandomness: number;
    flipXPercent: number;
    flipYPercent: number;
    flipOnBackfacesOnly: boolean;
    rotationRandomnessDeg: number;
    outputMinX: number;
    outputMinY: number;
    outputWidth: number;
    outputHeight: number;
    signal: AbortSignal;
    onProgress?: (info: PrimitivePointsProgress) => void;
}

interface PrimitiveVertex {
    position: Vector3;
    normal: Vector3;
    faceId: string;
}

interface PrimitiveGenerator {
    type: PrimitiveType;
    generate(subdivisions: number): PrimitiveVertex[];
}

const cubeGenerator: PrimitiveGenerator = {
    type: "cube",
    generate(subdivisions: number): PrimitiveVertex[] {
        const n = Math.max(1, Math.floor(subdivisions));
        const step = 1 / n;
        const half = 0.5;
        const keyToVertex = new Map<string, PrimitiveVertex>();
        const faces = [
            {
                axis: "x" as const,
                value: half,
                normal: new Vector3(1, 0, 0),
                faceId: "cube:x+",
            },
            {
                axis: "x" as const,
                value: -half,
                normal: new Vector3(-1, 0, 0),
                faceId: "cube:x-",
            },
            {
                axis: "y" as const,
                value: half,
                normal: new Vector3(0, 1, 0),
                faceId: "cube:y+",
            },
            {
                axis: "y" as const,
                value: -half,
                normal: new Vector3(0, -1, 0),
                faceId: "cube:y-",
            },
            {
                axis: "z" as const,
                value: half,
                normal: new Vector3(0, 0, 1),
                faceId: "cube:z+",
            },
            {
                axis: "z" as const,
                value: -half,
                normal: new Vector3(0, 0, -1),
                faceId: "cube:z-",
            },
        ];
        for (const face of faces) {
            for (let i = 0; i <= n; i++) {
                for (let j = 0; j <= n; j++) {
                    const a = -half + i * step;
                    const b = -half + j * step;
                    const p = new Vector3(0, 0, 0);
                    if (face.axis === "x") {
                        p.set(face.value, a, b);
                    } else if (face.axis === "y") {
                        p.set(a, face.value, b);
                    } else {
                        p.set(a, b, face.value);
                    }
                    const key = `${p.x.toFixed(6)}|${p.y.toFixed(6)}|${p.z.toFixed(6)}`;
                    if (!keyToVertex.has(key)) {
                        keyToVertex.set(key, {
                            position: p,
                            normal: face.normal.clone(),
                            faceId: face.faceId,
                        });
                    } else {
                        const v = keyToVertex.get(key);
                        if (v) v.normal.add(face.normal);
                    }
                }
            }
        }
        return [...keyToVertex.values()].map((v) => ({
            position: v.position,
            normal: v.normal.normalize(),
            faceId: v.faceId,
        }));
    },
};

function vertexKey(v: Vector3): string {
    return `${v.x.toFixed(6)}|${v.y.toFixed(6)}|${v.z.toFixed(6)}`;
}

function addOrAccumulateVertex(
    map: Map<string, PrimitiveVertex>,
    position: Vector3,
    normal: Vector3,
    faceId: string,
): void {
    const key = vertexKey(position);
    if (!map.has(key)) {
        map.set(key, {
            position,
            normal: normal.clone(),
            faceId,
        });
        return;
    }
    const existing = map.get(key);
    if (!existing) return;
    existing.normal.add(normal);
}

const primitiveRegistry: Record<PrimitiveType, PrimitiveGenerator> = {
    cube: cubeGenerator,
    "geodesic-sphere": {
        type: "geodesic-sphere",
        generate(subdivisions: number) {
            const detail = Math.max(0, Math.min(6, Math.floor(subdivisions)));
            const geometry = new IcosahedronGeometry(0.5, detail).toNonIndexed();
            const positions = geometry.getAttribute("position");
            const normals = geometry.getAttribute("normal");
            const deduped = new Map<string, PrimitiveVertex>();
            for (let i = 0; i < positions.count; i++) {
                const x = positions.getX(i);
                const y = positions.getY(i);
                const z = positions.getZ(i);
                const nx = normals.getX(i);
                const ny = normals.getY(i);
                const nz = normals.getZ(i);
                const faceIndex = Math.floor(i / 3);
                const key = `${x.toFixed(6)}|${y.toFixed(6)}|${z.toFixed(6)}`;
                if (!deduped.has(key)) {
                    deduped.set(key, {
                        position: new Vector3(x, y, z),
                        normal: new Vector3(nx, ny, nz).normalize(),
                        faceId: `geodesic:d${detail}:f${faceIndex}`,
                    });
                }
            }
            geometry.dispose();
            return [...deduped.values()];
        },
    },
    icosahedron: {
        type: "icosahedron",
        generate(subdivisions: number) {
            const n = Math.max(1, Math.min(32, Math.floor(subdivisions)));
            const geometry = new IcosahedronGeometry(0.5, 0).toNonIndexed();
            const positions = geometry.getAttribute("position");
            const deduped = new Map<string, PrimitiveVertex>();
            for (let i = 0; i < positions.count; i += 3) {
                const faceIndex = Math.floor(i / 3);
                const a = new Vector3(
                    positions.getX(i),
                    positions.getY(i),
                    positions.getZ(i),
                );
                const b = new Vector3(
                    positions.getX(i + 1),
                    positions.getY(i + 1),
                    positions.getZ(i + 1),
                );
                const c = new Vector3(
                    positions.getX(i + 2),
                    positions.getY(i + 2),
                    positions.getZ(i + 2),
                );
                const faceNormal = b
                    .clone()
                    .sub(a)
                    .cross(c.clone().sub(a))
                    .normalize();
                for (let bi = 0; bi <= n; bi++) {
                    for (let bj = 0; bj <= n - bi; bj++) {
                        const u = bi / n;
                        const v = bj / n;
                        const w = 1 - u - v;
                        const p = a
                            .clone()
                            .multiplyScalar(w)
                            .add(b.clone().multiplyScalar(u))
                            .add(c.clone().multiplyScalar(v));
                        addOrAccumulateVertex(
                            deduped,
                            p,
                            faceNormal,
                            `icosahedron:f${faceIndex}`,
                        );
                    }
                }
            }
            geometry.dispose();
            return [...deduped.values()].map((v) => ({
                position: v.position,
                normal: v.normal.normalize(),
                faceId: v.faceId,
            }));
        },
    },
    torus: {
        type: "torus",
        generate(subdivisions: number) {
            const radialSegments = Math.max(
                8,
                Math.min(96, Math.floor(subdivisions * 6)),
            );
            const tubularSegments = Math.max(
                12,
                Math.min(192, Math.floor(subdivisions * 10)),
            );
            const geometry = new TorusGeometry(
                0.42,
                0.16,
                radialSegments,
                tubularSegments,
            ).toNonIndexed();
            const positions = geometry.getAttribute("position");
            const normals = geometry.getAttribute("normal");
            const deduped = new Map<string, PrimitiveVertex>();
            for (let i = 0; i < positions.count; i++) {
                const x = positions.getX(i);
                const y = positions.getY(i);
                const z = positions.getZ(i);
                const nx = normals.getX(i);
                const ny = normals.getY(i);
                const nz = normals.getZ(i);
                const faceIndex = Math.floor(i / 3);
                const key = `${x.toFixed(6)}|${y.toFixed(6)}|${z.toFixed(6)}`;
                if (!deduped.has(key)) {
                    deduped.set(key, {
                        position: new Vector3(x, y, z),
                        normal: new Vector3(nx, ny, nz).normalize(),
                        faceId: `torus:f${faceIndex}`,
                    });
                }
            }
            geometry.dispose();
            return [...deduped.values()];
        },
    },
    capsule: {
        type: "capsule",
        generate(subdivisions: number) {
            const capSegments = Math.max(
                2,
                Math.min(24, Math.floor(subdivisions + 1)),
            );
            const radialSegments = Math.max(
                8,
                Math.min(128, Math.floor(subdivisions * 8)),
            );
            const geometry = new CapsuleGeometry(
                0.28,
                0.48,
                capSegments,
                radialSegments,
            ).toNonIndexed();
            const positions = geometry.getAttribute("position");
            const normals = geometry.getAttribute("normal");
            const deduped = new Map<string, PrimitiveVertex>();
            for (let i = 0; i < positions.count; i++) {
                const x = positions.getX(i);
                const y = positions.getY(i);
                const z = positions.getZ(i);
                const nx = normals.getX(i);
                const ny = normals.getY(i);
                const nz = normals.getZ(i);
                const faceIndex = Math.floor(i / 3);
                const key = `${x.toFixed(6)}|${y.toFixed(6)}|${z.toFixed(6)}`;
                if (!deduped.has(key)) {
                    deduped.set(key, {
                        position: new Vector3(x, y, z),
                        normal: new Vector3(nx, ny, nz).normalize(),
                        faceId: `capsule:f${faceIndex}`,
                    });
                }
            }
            geometry.dispose();
            return [...deduped.values()];
        },
    },
    pyramid: {
        type: "pyramid",
        generate(subdivisions: number) {
            const n = Math.max(1, Math.min(64, Math.floor(subdivisions)));
            const deduped = new Map<string, PrimitiveVertex>();
            const apex = new Vector3(0, 0.5, 0);
            const b0 = new Vector3(-0.5, -0.5, -0.5);
            const b1 = new Vector3(0.5, -0.5, -0.5);
            const b2 = new Vector3(0.5, -0.5, 0.5);
            const b3 = new Vector3(-0.5, -0.5, 0.5);
            const sideFaces: Array<[Vector3, Vector3, Vector3]> = [
                [apex, b0, b1],
                [apex, b1, b2],
                [apex, b2, b3],
                [apex, b3, b0],
            ];

            for (let faceIndex = 0; faceIndex < sideFaces.length; faceIndex++) {
                const [a, b, c] = sideFaces[faceIndex]!;
                const normal = b
                    .clone()
                    .sub(a)
                    .cross(c.clone().sub(a))
                    .normalize();
                for (let i = 0; i <= n; i++) {
                    for (let j = 0; j <= n - i; j++) {
                        const u = i / n;
                        const v = j / n;
                        const w = 1 - u - v;
                        const p = a
                            .clone()
                            .multiplyScalar(w)
                            .add(b.clone().multiplyScalar(u))
                            .add(c.clone().multiplyScalar(v));
                        addOrAccumulateVertex(
                            deduped,
                            p,
                            normal,
                            `pyramid:side:${faceIndex}`,
                        );
                    }
                }
            }

            const baseNormal = new Vector3(0, -1, 0);
            for (let i = 0; i <= n; i++) {
                for (let j = 0; j <= n; j++) {
                    const u = i / n;
                    const v = j / n;
                    const p = b0
                        .clone()
                        .multiplyScalar((1 - u) * (1 - v))
                        .add(b1.clone().multiplyScalar(u * (1 - v)))
                        .add(b2.clone().multiplyScalar(u * v))
                        .add(b3.clone().multiplyScalar((1 - u) * v));
                    addOrAccumulateVertex(deduped, p, baseNormal, "pyramid:base");
                }
            }

            return [...deduped.values()].map((v) => ({
                position: v.position,
                normal: v.normal.normalize(),
                faceId: v.faceId,
            }));
        },
    },
};

function throwIfAborted(signal: AbortSignal): void {
    if (signal.aborted) {
        throw new DOMException("Aborted", "AbortError");
    }
}

function assignObjectIndex(
    pointIndex: number,
    objectCount: number,
    mode: PrimitivePlacementMode,
): number {
    if (objectCount <= 1) return 0;
    if (mode === "alternating") return pointIndex % objectCount;
    const u = hashPlacementToUnit(0, pointIndex, 0xd12f9a43);
    return Math.min(objectCount - 1, Math.floor(u * objectCount));
}

function estimatePointCountForPrimitive(
    primitiveType: PrimitiveType,
    subdivisions: number,
): number {
    if (primitiveType === "cube") {
        const n = Math.max(1, Math.floor(subdivisions));
        return 6 * n * n + 2;
    }
    if (primitiveType === "geodesic-sphere") {
        // Icosahedron geodesic vertex count after subdivision detail d.
        const d = Math.max(0, Math.min(6, Math.floor(subdivisions)));
        return 10 * Math.pow(4, d) + 2;
    }
    if (primitiveType === "icosahedron") {
        const n = Math.max(1, Math.min(32, Math.floor(subdivisions)));
        return 10 * n * n + 2;
    }
    if (primitiveType === "torus") {
        const radialSegments = Math.max(
            8,
            Math.min(96, Math.floor(subdivisions * 6)),
        );
        const tubularSegments = Math.max(
            12,
            Math.min(192, Math.floor(subdivisions * 10)),
        );
        return radialSegments * tubularSegments;
    }
    if (primitiveType === "capsule") {
        const capSegments = Math.max(
            2,
            Math.min(24, Math.floor(subdivisions + 1)),
        );
        const radialSegments = Math.max(
            8,
            Math.min(128, Math.floor(subdivisions * 8)),
        );
        return radialSegments * (capSegments * 2 + 2);
    }
    if (primitiveType === "pyramid") {
        const n = Math.max(1, Math.min(64, Math.floor(subdivisions)));
        const perTriangle = ((n + 1) * (n + 2)) / 2;
        return 4 * perTriangle + (n + 1) * (n + 1) - 8 * n - 3;
    }
    return 0;
}

export function estimatePrimitivePointCount(
    primitiveType: PrimitiveType,
    subdivisions: number,
): number {
    return estimatePointCountForPrimitive(primitiveType, subdivisions);
}

export async function computePrimitivePointsPlacementsAsync(
    input: PrimitivePointsComputeInput,
): Promise<PrimitiveProjectedPoint[]> {
    const {
        primitiveType,
        subdivisions,
        rotationXDeg,
        rotationYDeg,
        rotationZDeg,
        cameraFovDeg,
        cameraDistance,
        backfaceCulling,
        objectCount,
        objectPlacementMode,
        objectRotationMode,
        polarRotationCenterXPct,
        polarRotationCenterYPct,
        positionOffsetRandomness,
        flipXPercent,
        flipYPercent,
        flipOnBackfacesOnly,
        rotationRandomnessDeg,
        outputMinX,
        outputMinY,
        outputWidth,
        outputHeight,
        signal,
        onProgress,
    } = input;
    throwIfAborted(signal);
    const generator = primitiveRegistry[primitiveType];
    if (!generator) return [];
    onProgress?.({
        phase: "generate",
        processed: 0,
        total: 1,
    });
    const baseVertices = generator.generate(subdivisions);
    throwIfAborted(signal);
    if (baseVertices.length === 0) return [];

    const camera = new PerspectiveCamera(
        Math.max(5, Math.min(170, cameraFovDeg)),
        outputWidth / Math.max(1, outputHeight),
        0.01,
        1000,
    );
    camera.position.set(0, 0, Math.max(0.1, cameraDistance));
    camera.lookAt(0, 0, 0);
    camera.updateMatrixWorld(true);
    camera.updateProjectionMatrix();

    const rotation = new Euler(
        (rotationXDeg * Math.PI) / 180,
        (rotationYDeg * Math.PI) / 180,
        (rotationZDeg * Math.PI) / 180,
        "XYZ",
    );
    const rotationMatrix = new Matrix4().makeRotationFromEuler(rotation);
    const normalMatrix = new Matrix3().setFromMatrix4(rotationMatrix);
    const worldToCamera = camera.position.clone();
    const projected: Array<{
        ndcX: number;
        ndcY: number;
        depth: number;
        faceId: string;
        isBackface: boolean;
    }> = [];
    const budget = startFrameBudget();
    onProgress?.({
        phase: "rotate-project",
        processed: 0,
        total: baseVertices.length,
    });
    for (let i = 0; i < baseVertices.length; i++) {
        await yieldIfFrameExhausted(budget, signal);
        const v = baseVertices[i];
        if (!v) continue;
        const worldPosition = v.position.clone().applyEuler(rotation);
        const worldNormal = v.normal.clone().applyMatrix3(normalMatrix).normalize();
        const toCamera = worldToCamera.clone().sub(worldPosition).normalize();
        const isBackface = worldNormal.dot(toCamera) <= 0;
        if (backfaceCulling) {
            if (isBackface) continue;
        }
        const clip = worldPosition.clone().project(camera);
        if (!Number.isFinite(clip.x) || !Number.isFinite(clip.y)) continue;
        if (clip.z < -1 || clip.z > 1) continue;
        projected.push({
            ndcX: clip.x,
            ndcY: clip.y,
            depth: clip.z,
            faceId: v.faceId,
            isBackface,
        });
        onProgress?.({
            phase: "rotate-project",
            processed: i + 1,
            total: baseVertices.length,
        });
    }
    if (projected.length === 0) return [];

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const p of projected) {
        minX = Math.min(minX, p.ndcX);
        maxX = Math.max(maxX, p.ndcX);
        minY = Math.min(minY, p.ndcY);
        maxY = Math.max(maxY, p.ndcY);
    }
    const pad = 0.1;
    minX -= pad;
    maxX += pad;
    minY -= pad;
    maxY += pad;
    const spanX = Math.max(1e-6, maxX - minX);
    const spanY = Math.max(1e-6, maxY - minY);
    projected.sort((a, b) =>
        a.ndcY === b.ndcY ? a.ndcX - b.ndcX : a.ndcY - b.ndcY,
    );
    onProgress?.({
        phase: "assign",
        processed: 0,
        total: projected.length,
    });
    const out: PrimitiveProjectedPoint[] = projected.map((p, i) => {
        const baseX = outputMinX + ((p.ndcX - minX) / spanX) * outputWidth;
        const baseY = outputMinY + (1 - (p.ndcY - minY) / spanY) * outputHeight;
        const jitterRange = Math.max(0, positionOffsetRandomness);
        const jitterX =
            jitterRange > 0
                ? (hashPlacementToUnit(0, i, 0x53a7f2c1) * 2 - 1) * jitterRange
                : 0;
        const jitterY =
            jitterRange > 0
                ? (hashPlacementToUnit(0, i, 0x7f4a7c15) * 2 - 1) * jitterRange
                : 0;
        const x = baseX + jitterX;
        const y = baseY + jitterY;
        const objectIndex = assignObjectIndex(i, objectCount, objectPlacementMode);
        const normalizedFlipXPercent = Math.max(0, Math.min(100, flipXPercent));
        const normalizedFlipYPercent = Math.max(0, Math.min(100, flipYPercent));
        const canFlip = !flipOnBackfacesOnly || p.isBackface;
        const flipX =
            canFlip &&
            hashPlacementToUnit(0, i, 0x2f6e2b1d) * 100 < normalizedFlipXPercent;
        const flipY =
            canFlip &&
            hashPlacementToUnit(0, i, 0x6ad9f42b) * 100 < normalizedFlipYPercent;

        let rotationDeg = 0;
        if (objectRotationMode === "path") {
            const prev = i > 0 ? projected[i - 1] : null;
            const next = i + 1 < projected.length ? projected[i + 1] : null;
            const a = prev ?? p;
            const b = next ?? p;
            const ax = outputMinX + ((a.ndcX - minX) / spanX) * outputWidth;
            const ay =
                outputMinY + (1 - (a.ndcY - minY) / spanY) * outputHeight;
            const bx = outputMinX + ((b.ndcX - minX) / spanX) * outputWidth;
            const by =
                outputMinY + (1 - (b.ndcY - minY) / spanY) * outputHeight;
            const dx = bx - ax;
            const dy = by - ay;
            if (Math.abs(dx) > 1e-6 || Math.abs(dy) > 1e-6) {
                rotationDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
            }
        } else if (objectRotationMode === "radial") {
            const cx = outputMinX + outputWidth * 0.5;
            const cy = outputMinY + outputHeight * 0.5;
            rotationDeg = (Math.atan2(y - cy, x - cx) * 180) / Math.PI;
        } else if (objectRotationMode === "random") {
            const u = hashPlacementToUnit(0, i, 0x9e3779b9);
            rotationDeg = u * 360;
        } else if (
            objectRotationMode === "polar-around-point" ||
            objectRotationMode === "polar-around-point-inverted"
        ) {
            const px =
                outputMinX + (outputWidth * polarRotationCenterXPct) / 100;
            const py =
                outputMinY + (outputHeight * polarRotationCenterYPct) / 100;
            const dx = x - px;
            const dy = y - py;
            const angleRad =
                objectRotationMode === "polar-around-point"
                    ? Math.atan2(dx, dy) // 0 at bottom, pi at top
                    : Math.atan2(dx, -dy); // 0 at top, pi at bottom
            rotationDeg = (angleRad * 180) / Math.PI;
        }
        const rotationJitterRange = Math.max(0, rotationRandomnessDeg);
        if (rotationJitterRange > 0) {
            const jitter =
                (hashPlacementToUnit(0, i, 0x4d31c9a7) * 2 - 1) *
                rotationJitterRange;
            rotationDeg += jitter;
        }
        rotationDeg = roundDeg(rotationDeg);
        return {
            x,
            y,
            depth: p.depth,
            objectIndex,
            rotationDeg,
            faceId: p.faceId,
            flipX,
            flipY,
            isBackface: p.isBackface,
        };
    });
    onProgress?.({
        phase: "assign",
        processed: out.length,
        total: out.length,
    });
    return out;
}
